import { useState, useMemo } from 'react';
import {
  Combobox,
  ComboboxInput,
  ComboboxOptions,
  ComboboxOption,
  Transition,
  TransitionChild,
  Dialog,
  DialogPanel,
  DialogTitle
} from '@headlessui/react';
import { MagnifyingGlassIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useStats } from '@/hooks/useStats';
import { useAppStore } from '@/store/appStore';
import { formatCityName } from '@/utils/formatters';
import Fuse from 'fuse.js';
import './CitySearch.css';

export const CitySearch = () => {
  const { data: statsData } = useStats();
  const { selectedCity, setSelectedCity, unavailableCities, removeCity } = useAppStore();
  const [query, setQuery] = useState('');
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  // Include all cities from stats, plus unavailable cities that are being prepared
  const allCities = statsData?.statistics.cities_used
    ? Object.keys(statsData.statistics.cities_used)
    : [];

  const availableCities = [
    ...allCities,
    ...unavailableCities.filter((city) => !allCities.some((c) => c.toLowerCase() === city.toLowerCase())),
  ];

  // Configure fuzzy search with Fuse.js
  const fuse = useMemo(
    () =>
      new Fuse(availableCities, {
        threshold: 0.3, // Lower = more strict matching
        distance: 100,
        ignoreLocation: true,
        keys: [''], // Search the string directly
      }),
    [availableCities]
  );

  // Filter cities based on search query
  const filteredCities = useMemo(() => {
    return query === ''
      ? availableCities
      : fuse.search(query).map((result) => result.item);
  }, [query, availableCities, fuse]);

  const handleSelect = (city: string | null) => {
    if (city) {
      setSelectedCity(city);
      setQuery('');
      setIsMobileOpen(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && query.trim() !== '') {
      // If user presses Enter with a custom city name
      const trimmedQuery = query.trim();
      // Check if the query matches an existing city (case-insensitive)
      const existingCity = availableCities.find(
        (city) => city.toLowerCase() === trimmedQuery.toLowerCase()
      );

      if (existingCity) {
        // Select the existing city
        handleSelect(existingCity);
      } else if (filteredCities.length === 0) {
        // No matches found, treat as a new custom city
        handleSelect(trimmedQuery);
      }
    }
  };

  const handleClear = () => {
    setSelectedCity(null);
    setQuery('');
  };

  const handleMobileOpen = () => {
    setIsMobileOpen(true);
  };

  const handleMobileClose = () => {
    setIsMobileOpen(false);
    setQuery('');
  };

  const handleDeleteCity = (e: React.MouseEvent | React.PointerEvent, city: string) => {
    e.stopPropagation();
    e.preventDefault();
    removeCity(city);
  };

  // Get status label for a city
  const getCityStatusLabel = (city: string): string | null => {
    const isPreparing = unavailableCities.some((c) => c.toLowerCase() === city.toLowerCase());
    return isPreparing ? 'Being Prepared' : null;
  };

  // Shared options rendering component
  const renderOptions = () => [
    filteredCities.length > 0 &&
      filteredCities.map((city) => {
        const statusLabel = getCityStatusLabel(city);
        const hasStatus = true;
        return (
          <ComboboxOption
            key={city}
            value={city}
            className={({ active }) =>
              `cursor-pointer select-none relative py-3 pl-10 pr-10 ${
                active ? 'bg-apple-blue/10 dark:bg-apple-darkblue/10' : ''
              }`
            }
          >
            {({ selected }) => (
              <>
                <div className="flex items-center justify-between gap-2">
                  <span
                    className={`city-option-text ${
                      selected ? 'selected' : 'not-selected'
                    }`}
                  >
                    {formatCityName(city)}
                  </span>
                  <div className="flex items-center gap-2">
                    {statusLabel && (
                      <span className={`city-status-label ${
                        statusLabel === 'Being Prepared'
                          ? 'preparing'
                          : 'normal'
                      }`}>
                        {statusLabel}
                      </span>
                    )}
                    {hasStatus && (
                      <button
                        type="button"
                        onPointerDown={(e) => handleDeleteCity(e, city)}
                        onMouseDown={(e) => handleDeleteCity(e, city)}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                        }}
                        className="p-1 text-apple-gray hover:text-red-500 dark:hover:text-red-400 transition-colors rounded-md hover:bg-red-50 dark:hover:bg-red-900/20"
                        aria-label={`Delete ${city}`}
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
                {selected && (
                  <CheckIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-apple-blue dark:text-apple-darkblue" />
                )}
              </>
            )}
          </ComboboxOption>
        );
      }),
    filteredCities.length === 0 && query !== '' && (
      <ComboboxOption
        key="add-custom-city"
        value={query.trim()}
        className={({ active }) =>
          `cursor-pointer select-none relative py-3 pl-4 pr-4 ${
            active ? 'bg-apple-blue/10 dark:bg-apple-darkblue/10' : ''
          }`
        }
      >
        <div className="flex items-center gap-2">
          <span className="text-apple-gray">Add:</span>
          <span className="font-semibold text-apple-dark dark:text-apple-light">
            {formatCityName(query.trim())}
          </span>
        </div>
      </ComboboxOption>
    ),
  ];

  return (
    <>
      {/* Desktop Version - Hidden on mobile */}
      <div className="hidden md:block">
        <Combobox value={selectedCity || ''} onChange={handleSelect}>
          <div className="relative">
            <div className="relative w-full">
              <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-apple-gray" />
              <ComboboxInput
                className="w-full pl-12 pr-12 py-3 bg-white/50 dark:bg-gray-900/50 backdrop-blur-md rounded-xl border border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-apple-blue dark:focus:ring-apple-darkblue text-apple-dark dark:text-apple-light placeholder:text-apple-gray"
                placeholder="Search for a city..."
                displayValue={(city: string) => formatCityName(city)}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              {selectedCity && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-apple-gray hover:text-apple-dark dark:hover:text-apple-light transition-colors"
                  aria-label="Clear city selection"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              )}
            </div>

            <Transition
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
              afterLeave={() => setQuery('')}
            >
              <div>
                <ComboboxOptions className="absolute mt-2 max-h-60 w-full overflow-auto rounded-xl bg-white dark:bg-gray-900 backdrop-blur-xl border border-gray-200 dark:border-gray-700 shadow-lg z-10">
                  {renderOptions()}
                </ComboboxOptions>
              </div>
            </Transition>
          </div>
        </Combobox>
      </div>

      {/* Mobile Version - Button that opens dialog */}
      <div className="md:hidden">
        <button
          onClick={handleMobileOpen}
          className="w-full flex items-center gap-3 pl-12 pr-4 py-3 bg-white/50 dark:bg-gray-900/50 backdrop-blur-md rounded-xl border border-gray-200 dark:border-gray-700 text-left text-apple-dark dark:text-apple-light"
        >
          <MagnifyingGlassIcon className="absolute left-4 h-5 w-5 text-apple-gray" />
          <span className={selectedCity ? '' : 'text-apple-gray'}>
            {selectedCity ? formatCityName(selectedCity) : 'Search for a city...'}
          </span>
        </button>

        {/* Mobile Dialog */}
        <Transition show={isMobileOpen}>
          <Dialog onClose={handleMobileClose} className="relative z-50">
            <TransitionChild
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
            </TransitionChild>

            <div className="fixed inset-0 overflow-y-auto">
              <TransitionChild
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <div>
                  <DialogPanel className="min-h-screen bg-apple-light dark:bg-black">
                    <div className="sticky top-0 bg-apple-light/95 dark:bg-black/95 backdrop-blur-lg border-b border-gray-200 dark:border-gray-800 px-4 py-4">
                      <div className="flex items-center justify-between mb-4">
                        <DialogTitle className="text-lg font-semibold text-apple-dark dark:text-apple-light">
                          Select City
                        </DialogTitle>
                        <button
                          onClick={handleMobileClose}
                          className="text-apple-gray hover:text-apple-dark dark:hover:text-apple-light transition-colors"
                          aria-label="Close"
                        >
                          <XMarkIcon className="h-6 w-6" />
                        </button>
                      </div>

                      <Combobox value={selectedCity || ''} onChange={handleSelect}>
                        <div className="relative">
                          <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-apple-gray" />
                          <ComboboxInput
                            className="w-full pl-12 pr-4 py-3 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-apple-blue dark:focus:ring-apple-darkblue text-apple-dark dark:text-apple-light placeholder:text-apple-gray"
                            placeholder="Search for a city..."
                            displayValue={(city: string) => formatCityName(city)}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={handleKeyDown}
                            autoFocus
                          />
                        </div>

                        <ComboboxOptions static className="mt-4 max-h-[calc(100vh-12rem)] overflow-auto">
                          {renderOptions()}
                        </ComboboxOptions>
                      </Combobox>
                    </div>
                  </DialogPanel>
                </div>
              </TransitionChild>
            </div>
          </Dialog>
        </Transition>
      </div>
    </>
  );
};
